import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import EvidenceResultRow from './EvidenceResultRow';

const MODE_NAMES = { HP: 'Hardpoint', SND: 'Search & Destroy', CTL: 'Control' };
const rate = (value) => value === null || value === undefined ? '-' : `${value.toFixed(1)}%`;
const recordText = (value) => value?.total ? `${value.wins}-${value.losses}` : '-';
const metricText = (value) => value === null || value === undefined ? '-' : value.toFixed(1);

const DashboardEvidenceDrawer = ({ selection, scope, onClose, returnFocusTo }) => {
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(10);
  const [showEarlier, setShowEarlier] = useState(false);
  const isMap = selection.kind === 'map';
  const data = isMap ? selection.map : selection.flag.evidence;
  const isPlayer = !isMap && selection.flag.type === 'playerTrend';
  const recentEntries = data.recentEntries || [];
  const baselineEntries = data.baselineEntries || [];
  const history = data.historyEntries || [];
  const title = isPlayer ? data.name : [MODE_NAMES[data.mode] || data.mode, data.map].filter(Boolean).join(' / ');

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const appRoot = document.getElementById('root');
    const previousInert = appRoot?.inert;
    if (appRoot) appRoot.inert = true;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); return; }
      if (event.key !== 'Tab' || !panelRef.current) return;
      const focusable = [...panelRef.current.querySelectorAll('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      if (appRoot) appRoot.inert = previousInert;
      returnFocusTo?.focus();
    };
  }, [onClose, returnFocusTo]);

  const summary = (label, value, count) => <div className="v2-evidence-stat">
    <span>{label}</span>
    <strong>{isPlayer ? metricText(value) : recordText(value)}</strong>
    <small>{isPlayer ? `${count} entries` : `${rate(value?.rate)} / ${value?.total ?? 0} maps`}</small>
  </div>;
  const change = isMap ? data.delta : selection.flag.delta;
  const deltaText = change === null || change === undefined ? 'Not enough earlier maps' :
    `${change > 0 ? '+' : ''}${change.toFixed(1)} ${isPlayer ? '%' : 'pp'}`;

  return createPortal(<div className="v2-evidence-overlay" onMouseDown={(event) => {
    if (event.target === event.currentTarget) onClose();
  }}>
    <aside ref={panelRef} className="v2-evidence-drawer" role="dialog" aria-modal="true" aria-labelledby="v2-evidence-title">
      <header className="v2-evidence-head">
        <div><span>{isMap ? 'Map Performance' : isPlayer ? `${data.mode} ${data.metric} trend` : 'Performance Trend'}</span>
          <h2 id="v2-evidence-title">{title}</h2></div>
        <button ref={closeRef} type="button" className="v2-evidence-close" onClick={onClose} aria-label="Close evidence"><X size={20} /></button>
      </header>
      <div className="v2-evidence-body">
        <p className="v2-evidence-scope"><strong>Filtered view</strong> {scope}</p>
        <div className="v2-evidence-summary">
          {isMap && summary('Overall', data.record)}
          {summary('Recent', data.recent, isPlayer ? data.recentCount : undefined)}
          {summary('Earlier', data.baseline, isPlayer ? data.baselineCount : undefined)}
          <div className="v2-evidence-stat"><span>Change</span><strong>{deltaText}</strong><small>Recent vs earlier</small></div>
        </div>
        {isMap ? <section className="v2-evidence-list-section">
          <h3>Result History <small>{history.length} dated maps</small></h3>
          {history.length ? <ol>{history.slice(0, visibleCount).map((row, index) =>
            <EvidenceResultRow key={`${row.ModeID || row.Date}-${index}`} row={row} />)}</ol> :
            <p>No dated results in this filter.</p>}
          {visibleCount < history.length && <button type="button" className="v2-evidence-more" onClick={() => setVisibleCount((count) => count + 10)}>Show More</button>}
          {!!data.undatedEntries?.length && <p>{data.undatedEntries.length} undated map result(s) count toward the overall record but cannot be ordered in history.</p>}
        </section> : <>
          <section className="v2-evidence-list-section"><h3>Recent Evidence <small>{recentEntries.length} {isPlayer ? 'entries' : 'maps'}</small></h3>
            {recentEntries.length ? <ol>{recentEntries.map((row, index) => <EvidenceResultRow key={`${row.ModeID || row.Date}-${index}`} row={row} metric={isPlayer ? data.metric : undefined} />)}</ol> : <p>No dated evidence available.</p>}
          </section>
          {!!baselineEntries.length && <section className="v2-evidence-list-section">
            <button type="button" className="v2-evidence-earlier" aria-expanded={showEarlier} onClick={() => setShowEarlier((value) => !value)}>
              {showEarlier ? 'Hide' : 'Show'} Earlier Evidence ({baselineEntries.length} {isPlayer ? 'entries' : 'maps'})
            </button>
            {showEarlier && <ol>{baselineEntries.map((row, index) => <EvidenceResultRow key={`${row.ModeID || row.Date}-${index}`} row={row} metric={isPlayer ? data.metric : undefined} />)}</ol>}
          </section>}
        </>}
      </div>
    </aside>
  </div>, document.body);
};

export default DashboardEvidenceDrawer;
